// Shell del wizard (pantalla completa, sin sidebar del portal): header con marca + stepper, y el
// contenido del paso como children. La sesión SIEMPRE se re-lee del silo (fuente de verdad).
//
// «Guardar y salir» NO vive aquí: el mock lo pone en el pie de cada card. El shell solo monta el
// modal y lo ofrece por contexto, para que lo abran el pie del paso y la flecha de la topbar.
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { useWizardSession } from '../api/useWizard';
import { WizardTopbar } from './WizardTopbar';
import { WizardStepper } from './WizardStepper';
import { WizardClientBar } from './WizardClientBar';
import { ProspectDrawer } from './ProspectDrawer';
import { CancelSaleDialog } from './CancelSaleDialog';
import { canCancelSale } from '../cancelSale';
import { hasAccountOpeningMark } from '../accountOpening';
import { WizardExitProvider, type WizardExitApi } from './WizardExit';
import { DRAWER_STEPS } from '../prospects';
import { stepBySlug } from '../steps';
import type { WizardStepSlug } from '../types';

export function WizardShell({ sessionId, children }: { sessionId: string; children: ReactNode }) {
    const { data: session, isLoading } = useWizardSession(sessionId);
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    // Slug del paso en la URL (param del route hijo `$step`) — el stepper marca "current" por la
    // pantalla visible, no por el checkpoint del backend.
    const { step: stepSlug } = useParams({ strict: false });

    // El paso visible manda, no `session.currentStep`: el checkpoint del backend avanza al
    // CONFIRMAR, así que iba un paso atrás y «Guardar y salir» aparecía en pantallas que no lo
    // admiten y faltaba en las que sí (02, 06, 07 y 09).
    const visibleStep = stepBySlug(stepSlug as WizardStepSlug)?.id ?? session?.currentStep;
    // La apertura no se guarda como prospecto: sin «Guardar y salir» en ningún paso.
    const accountOpening = !!session && hasAccountOpeningMark(session);
    const exitApi = useMemo<WizardExitApi>(
        () => ({
            canSaveProspect:
                !accountOpening && visibleStep !== undefined && DRAWER_STEPS.includes(visibleStep),
            openProspectDrawer: () => {
                setDrawerOpen(true);
            },
            canCancelSale: canCancelSale(session),
            openCancelDialog: () => {
                setCancelOpen(true);
            },
        }),
        [visibleStep, session, accountOpening],
    );

    if (isLoading) return <Loading size="lg" fullScreen />;
    // Cuelga de que falte la data, no del estado de error: la query no fija `staleTime` y refresca
    // al volver el foco, así que un refetch fallido tumbaba el wizard con la venta a medias.
    if (!session) {
        return (
            <div className="flex h-[100dvh] flex-col items-center justify-center gap-3 px-5 text-center">
                <p className="text-sm text-muted-foreground">No encontramos esta venta.</p>
                {/* La otra salida: el prospecto puede apuntar a una venta que el TTL ya borró, y el
                    vendedor tiene el teléfono del cliente a mano. */}
                <Button
                    size="sm"
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() => void navigate({ to: '/wizard' })}
                >
                    Iniciar una venta por teléfono
                </Button>
                <Link to="/sales" className="text-sm font-semibold text-primary underline">
                    Volver al portal
                </Link>
            </div>
        );
    }

    return (
        <WizardExitProvider value={exitApi}>
            <div
                data-wizard-root
                className="flex h-[100dvh] flex-col overflow-hidden bg-muted/40 text-foreground"
            >
                {/* .wizard-topbar del mock: UNA fila — marca a la izquierda, stepper a la derecha. */}
                <header className="flex-none border-b border-border bg-card px-3 py-2 sm:px-5 sm:py-3">
                    <WizardTopbar accountOpening={accountOpening}>
                        <WizardStepper session={session} currentSlug={stepSlug} />
                    </WizardTopbar>
                </header>

                <WizardClientBar session={session} />

                {/* .wizard-body: card arriba (no centrado vertical), padding 24/20/40. */}
                <main className="flex-1 overflow-y-auto">
                    <div className="flex min-h-full flex-col items-center px-3 pt-4 pb-8 sm:px-5 sm:pt-6 sm:pb-10">
                        {children}
                    </div>
                </main>

                {!accountOpening && (
                    <ProspectDrawer
                        session={session}
                        currentSlug={stepBySlug(stepSlug as WizardStepSlug)?.slug}
                        open={drawerOpen}
                        onClose={() => {
                            setDrawerOpen(false);
                        }}
                    />
                )}

                <CancelSaleDialog
                    sessionId={session.sessionId}
                    open={cancelOpen}
                    accountOpening={accountOpening}
                    onClose={() => {
                        setCancelOpen(false);
                    }}
                />
            </div>
        </WizardExitProvider>
    );
}
